// Angular + external
import {
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injector,
  NgZone,
  ViewEncapsulation,
} from "@angular/core";

// BFX
import { BfxBaseBlotter, provideBlotter } from "../bfx-base-blotter/bfx-base-blotter";
// placeholder renderer - while autoheight shakes out
import { OpsIntTooltip } from "./renderers/OpsIntTooltip";
// services
import {
  BfxBlotterOpsIntService,
  BLOTTER_EVENT_TIME_FIELD,
} from "./services/bfx-blotter-ops-int.service";
import { TranslateService } from "@ngx-translate/core";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { BfxBlotterOpsIntContextMenuService } from "./services/bfx-blotter-ops-int-contextmenu.service";
import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";
import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxBlotterOpsIntFilterService } from "./services/bfx-blotter-ops-int-filter.service";

// form workers
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  OpsIntTableDefinitionFactory,
  SystemConfigPromiseService,
  ProfilePromiseActionService,
  BfxBlotterAction,
  ContextMenuItemState,
} from "shared/cubiq-services";
import { BfxMouseModifierKeyService } from "../bfx-common/services/bfx-mouse-modifier-key.service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BfxSaveProfileNotificationService } from "../bfx-common/services/bfx-save-profile-notification.service";
import {
  GLComponentAlertStatus,
  GoldenLayoutComponent,
} from "../golden-layout/golden-layout-component";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";
import { FilterConfiguration } from "../bfx-blotter-filter/FilterConfiguration";

@Component({
  selector: "bfx-blotter-ops-int",
  templateUrl: "./html/bfx-blotter-ops-int.html",
  encapsulation: ViewEncapsulation.None,
  providers: [provideBlotter(BfxBlotterOpsIntComponent)],
  viewProviders: [BfxBaseBlotterColumnSelectorService],
  standalone: false,
})
export class BfxBlotterOpsIntComponent extends BfxBaseBlotter implements GoldenLayoutComponent {
  private lastItemCounter: number = -1;
  /** Is the view currently active? Inactive if in a hidden tab or inactive window */
  private viewActive: boolean = false;
  /** Are new alerts available since tab last focused? */
  public newAlert: boolean = false;

  /** read ID of latest message when tab last lost focus or had messages marked as seen */
  private maxReadId: number = 0;

  public frameworkComponents: any = {
    opsIntTooltip: OpsIntTooltip,
  };
  opsId: any;

  constructor(
    BfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    public service: BfxBlotterOpsIntService,
    _contextMenuService: BfxBlotterOpsIntContextMenuService,
    zone: NgZone,
    _tableDefinitionFactory: OpsIntTableDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    filteringService: BfxBlotterOpsIntFilterService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    sysConfigService: SystemConfigPromiseService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    public goldenLayoutService: GoldenLayoutService,
    protected userProfileService: BfxUserProfileService,
    protected cd: ChangeDetectorRef, // public opsCommsService: BfxCommsOpsIntService,
  ) {
    super(
      BfxBlotterElement,
      applicationRef,
      injector,
      service,
      _contextMenuService,
      zone,
      _tableDefinitionFactory,
      layoutService,
      translationService,
      blotterFilterService,
      filteringService,
      columnSelectorService,
      profileActionService,
      sysConfigService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
    );
    this.blotterId = "blotter-ops-int";

    this.maxReadId = this.service.getMaxViewedId();

    this.checkNewAlert();
  }

  public glInit(data: any, container: any, state: any): void {
    this.opsId = data;
  }

  protected initializeInMemory(): void {
    super.initializeInMemory();

    this.service.isReady().then(() => {
      this.blotterFilterService.applyFilterConfig({
        blotterId: this.blotterId,
        items: this.service.getFilterConfiguration(),
      });

      this.service.subjectNewItems.subscribe(() => {
        this.checkNewAlert();
      });
      this.filterSelectionChanged(this.service.getFilterConfiguration());
    });
  }

  public getBlotterType(): CubiqTS.BlotterType {
    return CubiqTS.BlotterType.OPSINTERVENTION;
  }

  protected initializeColumnDefinitions(): void {
    super.initializeColumnDefinitions();

    this.gridOptions.enableBrowserTooltips = false;

    this.gridOptions.getRowClass = function (this: BfxBlotterOpsIntComponent, params) {
      return "ops-int-data-row";
    }.bind(this);
    this.gridOptions.rowClassRules = {
      "ops-errors": (params) => {
        return (
          params != null &&
          params.data != null &&
          params.data instanceof CubiqTS.OpsIntEventImpl &&
          params.data.opsErrors != null &&
          params.data.opsErrors instanceof CubiqTS.ErrorsImpl
        );
      },
      "ops-int-new": (params) => params.data?.[BLOTTER_EVENT_TIME_FIELD] > this.maxReadId,
    };
    this.cd.markForCheck();
  }

  private checkBadgeNumber() {
    if (this.gridOptions.api) {
      if (this.lastItemCounter !== this.getBadgeNumber()) {
        // this.newAlert = true;
        this.checkNewAlert();
        this.publishBadgeUpdate();
      }
    }
  }

  protected override gridDataUpdated(): void {
    super.gridDataUpdated();
    this.checkBadgeNumber();
  }

  protected afterFilteringCompleted(): void {
    super.afterFilteringCompleted();
    this.publishBadgeUpdate();
  }

  protected publishBadgeUpdate() {
    this.lastItemCounter = this.getBadgeNumber();
    let alertStatus = this.getAlertStatus();
    this.layoutService.updateBadge(this, this.lastItemCounter, alertStatus);
  }

  public getBadgeNumber(): number {
    if (this.gridOptions && this.gridOptions.api) {
      return this.gridOptions.api.getDisplayedRowCount();
    }

    return 0;
  }

  public componentActivated(): void {
    this.viewActive = true;
    // this.newAlert = false;
  }

  public componentDeactivated(): void {
    if (this.viewActive) {
      // If it was active, then ensure that new alerts are disabled for what we currently show
      this.markAllAsSeen();
      this.viewActive = false;
    }
  }

  public getAlertStatus(): GLComponentAlertStatus {
    if (this.getBadgeNumber() == 0 || !this.newAlert) {
      // no alert if no message or all have been seen
      return GLComponentAlertStatus.NONE;
    }

    if (!this.viewActive && this.newAlert) {
      return GLComponentAlertStatus.ALERT_FLASH;
    }

    return GLComponentAlertStatus.ALERT;
  }

  public filterSelectionChanged(filterConfiguration: FilterConfiguration) {
    this.service.updateFilterSelection(filterConfiguration ?? []);
    this.service.updateBadgeCount(this);
    super.filterSelectionChanged(filterConfiguration);
  }

  public getFilterConfiguration(): string[] {
    return this.service.getFilterConfiguration();
  }

  public toggleSearch(el?: any): void {
    this.showSearch = !this.showSearch;
    if (!this.showSearch) {
      this.searchValue = "";
      this.onChangeSearchValue();
    }
  }

  public onChangeSearchValue() {
    this.search.next();
  }

  /**
   * External filter is always present as we have ops-int filtering
   * added as external filter.
   */
  public isExternalFilterPresent(): boolean {
    return true;
  }

  public doesExternalFilterPass(node: any) {
    return this.service.doesExternalFilterPass(node);
  }
  public restoreFromConfiguration(config: any): void {
    // Filters from service configuration
    if (config != null) {
      delete config.filterConfiguration;
    }

    super.restoreFromConfiguration(config);
  }

  public getComponentConfiguration(): any {
    let configuration = super.getComponentConfiguration();

    // Filters from service configuration
    if (configuration != null) {
      delete configuration.filterConfiguration;
    }

    return configuration;
  }

  public onCellDoubleClicked(event) {
    if (
      event === Object(event) &&
      event.node === Object(event.node) &&
      event.node.data != null &&
      event.node.data instanceof CubiqTS.OpsIntEventImpl &&
      ((event.node.data.info != null &&
        event.node.data.info instanceof CubiqTS.OpsExcInfoImpl &&
        event.node.data.info.deal != null &&
        event.node.data.info.deal instanceof CubiqTS.DealImpl &&
        CubiqTS.Long.isLong(event.node.data.info.deal.id)) ||
        (event.node.data.info != null &&
          event.node.data.info instanceof CubiqTS.OpsExcInfoImpl &&
          event.node.data.info.orderData != null &&
          event.node.data.info.orderData instanceof CubiqTS.OrderDataImpl &&
          CubiqTS.Long.isLong(event.node.data.info.orderData.orderId)))
    ) {
      let itemState: any = new ContextMenuItemState(1);
      let dealId =
        event.node.data.info instanceof CubiqTS.OpsExcInfoImpl &&
        event.node.data.info.deal instanceof CubiqTS.DealImpl &&
        CubiqTS.Long.isLong(event.node.data.info.deal.id)
          ? event.node.data.info.deal.id.toString()
          : null;
      let orderId = null;

      if (dealId == null)
        orderId =
          event.node.data.info instanceof CubiqTS.OpsExcInfoImpl &&
          event.node.data.info.orderData instanceof CubiqTS.OrderDataImpl &&
          CubiqTS.Long.isLong(event.node.data.info.orderData.orderId)
            ? event.node.data.info.orderData.orderId.toString()
            : null;

      if (dealId != null) {
        itemState.referencedDealIds = [dealId];
      } else if (orderId != null) {
        itemState.referencedOrderIds = [orderId];
      }

      this.contextMenuService.fireActionEvent(
        BfxBlotterAction.ACTION_OPS_INT_VIEW_DETAILS,
        this,
        [event.node.id],
        [event.node.data],
        itemState,
        null,
        event,
      );
    } else {
      logger.out.warn(
        `BfxBlotterOpsInt: ignoring double click for intervention event ${event.node.id} without order or deal id`,
      );
    }
  }

  protected applyColumnDefinitions() {
    super.applyColumnDefinitions();

    this.cd.detectChanges();
  }

  public showColumnSelector() {
    // no-op no column selector
  }

  public handleClick(event) {
    // no-op no column selector
  }

  public getTabIdentifier(): string {
    return "BfxBlotterOpsIntComponent";
  }

  /**
   * Remove the newAlert flag and set the lastRead eventId to the latest in the service
   */
  public markAllAsSeen() {
    this.newAlert = false;
    const newEventId = this.service.getMaxEventId();
    if (newEventId !== this.maxReadId) {
      this.maxReadId = newEventId;
      this.service.updateMaxViewedId();
      this.gridOptions.api.redrawRows();
    }
    this.publishBadgeUpdate();
  }

  /**
   * Set the new alert flag if service has recorded any new messages since our
   * last event id
   */
  private checkNewAlert() {
    const newEventId = this.service.getMaxEventId();
    this.newAlert = this.newAlert || newEventId > this.maxReadId;
  }
}
